import React, { useState, useEffect, useReducer } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { breakpoints, Body, Box, Card, FilterBar, Filter, Pagination, SearchResultsGrid } from 'dma-ui-kit';
import styled from 'styled-components';
import { translateInterfaceText } from 'helpers/translation';
import { filterQuery, history, unpackFilterQuery } from 'helpers/search';
import { reducer as queryReducer } from 'search/queries';
import { emptyCalendarResults, fetchCalendarResults } from 'search/calendarSearch';
import ImageNotFound from 'assets/resultmissing.png';
import useBreakpoint from 'use-breakpoint';

const Container = styled(Box)`
  padding: 0 12px;

  @media (min-width: 1025px) {
      padding: 0 48px;
  }
  @media (max-width: 1024px) {
      padding: 0 24px;
  }    

  margin-bottom: 10px;
  display: block;
  padding: 48px;
`;

const PaginationContainer = styled(Box)`
  max-width: 320px;
  margin: auto;
  margin-top: 48px;

  @media (min-width: 1025px) {
      max-width: 800px;
  }
  @media (max-width: 1024px) {
      max-width: 640px;
  }    
`;

function compareKeys(a, b) {
  return (a.value < b.value) ? -1 : (a.value > b.value) ? 1 : 0;
}

const EventCalendar = React.memo(() => {
  const calendarEndpoint = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/calendar`;

    const noResultsText = translateInterfaceText("No Results Found");
    const loadingText = translateInterfaceText("Loading");

    const { search } = useLocation();
    const params = new URLSearchParams(search);

    const initialTerm = params.get('s');
    const initialFilter = unpackFilterQuery(params.get('f'));
    const initialPage = parseInt(params.get('p'), 10) || 1;

    const [results, setResults] = useState(emptyCalendarResults);
    const [loading, setLoading] = useState(false);
    const [filterAggregations, setFilterAggregations] = useState({});

    const date = new Date();

    const [options, dispatch] = useReducer(
      queryReducer,
      {  
        type: 'event',
        page: initialPage,
        term: initialTerm || '',
        limit: 9,
        filters: initialFilter || { 
          dateRange: [
            new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString().slice(0, 10),
            new Date(date.getFullYear(), date.getMonth()+3, date.getDate()).toISOString().slice(0, 10)
          ] 
        }
      },  
    );

    // Serialize date range and term so the aggregation effect only re-runs when either changes
    const dateRangeKey = options.filters?.dateRange?.join(',') ?? '';
    const aggKey = `${dateRangeKey}|${options.term}`;

    useEffect(() => {
      let active = true;

      const fetchAggregations = async () => {
        const aggResults = await fetchCalendarResults(calendarEndpoint, {
          limit: 1,
          page: 1,
          term: options.term,
          filters: { dateRange: options.filters?.dateRange },
        });

        if (active) {
          setFilterAggregations(aggResults.aggregations);
        }
      };

      fetchAggregations();

      return () => { active = false; };
    }, [calendarEndpoint, aggKey]); // eslint-disable-line

    useEffect(() => {
      let sectionActive = true;

      const fetchResults = async () => {
        setLoading(true);
        const nextResults = await fetchCalendarResults(calendarEndpoint, options);

        if (sectionActive) {
          setResults(nextResults);
          setLoading(false);
        }
      };

      fetchResults();

      return () => {
        sectionActive = false;
      };
    }, [calendarEndpoint, options]);
  
    const handleCombinedFilterChange = val => {
      const selectedOption = filterOptions.find(opt => opt.value === val);
      const { audiences: _a, category: _c, ...otherFilters } = options.filters || {};

      const payload = (val && selectedOption?.type)
        ? { ...otherFilters, [selectedOption.type]: [val] }
        : otherFilters;

      dispatch({ type: 'setFilters', payload });
      history.push(`/visit/calendar?s=${options.term}&f=${filterQuery(payload)}&p=1`);
    };

    const handleRangeChange = val => {
      const payload = { dateRange: val };
      dispatch({ type: 'setFilters', payload: payload});
      history.push(`/visit/calendar?s=${options.term}&f=${filterQuery(payload)}&p=1`);
    };

    const handleTermChange = val => {
      dispatch({ type: 'setTerm', payload: val });
      history.push(`/visit/calendar?s=${val}&f=${filterQuery(options.filters)}&p=1`);
    };

    const handlePageChange = val => {
      dispatch({ type: 'setPagination', payload: { page: val, limit: options.limit }});
      history.push(`/visit/calendar?s=${options.term}&f=${filterQuery(options.filters)}&p=${val}`);
      window.scrollTo(0, 0);
    };

    const filterOptions = [{ label: 'All Events', value: '' }, {} ];

    if (filterAggregations?.audiences)
      filterAggregations.audiences.sort(compareKeys).forEach(agg => {
        filterOptions.push({ label: agg.value, type: 'audiences', value: agg.value });
      });

    if (filterAggregations?.audiences?.length > 0 && filterAggregations?.categories?.length > 0)
      filterOptions.push({});

    if (filterAggregations?.categories)
      filterAggregations.categories.sort(compareKeys).forEach(agg => {
        filterOptions.push({ label: agg.value, type: 'category', value: agg.value });
      });

    const dateRangeDefaults = (options.filters?.dateRange) ? { from: options.filters.dateRange[0], to: options.filters.dateRange[1] } : undefined;
    const combinedFilterDefault = options.filters?.audiences?.[0]
      ? { value: options.filters.audiences[0] }
      : options.filters?.category?.[0]
        ? { value: options.filters.category[0] }
        : undefined;

    const { breakpoint } = useBreakpoint(breakpoints);
    const getCardsPerRow = () => {
        switch(breakpoint) {
            case "mobile":
                return 1;
            case "tablet":
            case "tabletSmall":
                return 2;
            default:
                return 3;
        };
    };

    return (
      <Container>
        <>
          <Box mb="24px">
            <FilterBar showSearchLabel={false} onSearch={handleTermChange} value={options.term}> 
              <Filter variant="dateRange" defaults={dateRangeDefaults} onChange={handleRangeChange} />
              <Filter options={filterOptions} onChange={handleCombinedFilterChange} defaults={combinedFilterDefault} />
            </FilterBar>
          </Box>
          {results?.hits && results.hits.length > 0 && !loading && (
            <SearchResultsGrid cardsPerRow={getCardsPerRow()} mb={"24px"}>
              {results.hits.map((result, index) => (
                <Link key={index} to={{ pathname: result.url }}>
                  <Card 
                    variant="calendar"
                    headline={result.category}
                    title={result.field_caption_text || result.title}
                    caption={<Body variant="small">{result.date}</Body>}
                    image={result.thumbnail || result.featured_image || ImageNotFound}
                    square
                    />
                  </Link>
              ))}
            </SearchResultsGrid>
          )}
          {results?.hits?.length === 0 && !loading && (
            <Box w="100%" pt="48px">
              <Body textAlign="center">{noResultsText}</Body>
            </Box>
          )}
          {loading && (
            <Box w="100%" pt="48px">
              <Body textAlign="center">{loadingText}...</Body>
            </Box>
          )}
          <PaginationContainer>
            <Pagination
              currentPage={results?.currentPage || 1}
              pageCount={results?.pageCount || 1}
              onChange={handlePageChange}
            />
          </PaginationContainer>
        </>
      </Container>
    );
});

export default EventCalendar;
